import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firestore, storage } from '../firebase';
import './PhotoCapture.css';

export default function PhotoCapture() {
  const [searchParams, setSearchParams] = useSearchParams();
  const eventId = searchParams.get('eventId');

  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  // phase: select | camera | preview | done
  const [phase, setPhase] = useState('select');
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [capturedObjectUrl, setCapturedObjectUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [lastUploadedName, setLastUploadedName] = useState('');
  /** @type {Array<{ teamId: string, url?: string }>} */
  const [memoryTeamPhotos, setMemoryTeamPhotos] = useState([]);

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const selectedTeam = teams.find(t => t.id === selectedTeamId) ?? null;

  const refreshMemoryTeamPhotos = useCallback(async () => {
    if (!eventId) {
      setMemoryTeamPhotos([]);
      return;
    }
    const metaSnap = await getDoc(doc(firestore, 'eventMemoryMeta', eventId));
    const arr = metaSnap.exists() ? metaSnap.data().teamPhotos : [];
    const normalized = Array.isArray(arr)
      ? arr.filter((e) => e?.teamId && e?.url)
      : [];
    setMemoryTeamPhotos(normalized);
  }, [eventId]);

  useEffect(() => {
    refreshMemoryTeamPhotos();
  }, [refreshMemoryTeamPhotos]);

  const teamIdsWithPhoto = useMemo(
    () => new Set(memoryTeamPhotos.map((p) => p.teamId)),
    [memoryTeamPhotos]
  );

  const teamsWithPhotoSorted = useMemo(
    () =>
      teams
        .filter((t) => teamIdsWithPhoto.has(t.id))
        .sort((a, b) => Number(a.teamNumber) - Number(b.teamNumber)),
    [teams, teamIdsWithPhoto]
  );

  const photoCountSummary = useMemo(() => {
    const total = teams.length;
    if (!total) return '';
    const n = teamIdsWithPhoto.size;
    if (n === 0) return `No team photos yet (${total} team${total === 1 ? '' : 's'})`;
    if (total === 1) return 'This team has a photo';
    if (n === 1) return `1 of ${total} teams has a photo`;
    return `${n} of ${total} teams have photos`;
  }, [teams.length, teamIdsWithPhoto]);

  // Load events when no eventId in URL
  useEffect(() => {
    if (eventId) return;
    getDocs(collection(firestore, 'events')).then(snap => {
      const evts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      evts.sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
      setEvents(evts);
    });
  }, [eventId]);

  // Load teams for selected event
  useEffect(() => {
    if (!eventId) { setTeams([]); return; }
    const q = query(collection(firestore, 'teams'), where('eventId', '==', eventId));
    getDocs(q).then(snap => {
      const t = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(tm => !tm.test)
        .sort((a, b) => Number(a.teamNumber) - Number(b.teamNumber));
      setTeams(t);
    });
  }, [eventId]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const attachStreamToVideo = useCallback(async (stream) => {
    const video = videoRef.current;
    if (!video) return;

    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.muted = true;
    video.autoplay = true;

    if (video.srcObject !== stream) video.srcObject = stream;

    await new Promise(resolve => {
      if (video.readyState >= 1) {
        resolve();
        return;
      }
      const onLoaded = () => {
        video.removeEventListener('loadedmetadata', onLoaded);
        resolve();
      };
      video.addEventListener('loadedmetadata', onLoaded);
    });

    await video.play().catch(() => {});

    await new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          window.clearInterval(timer);
          resolve();
          return;
        }
        if (Date.now() - startedAt > 1800) {
          window.clearInterval(timer);
          reject(new Error('Camera stream attached but no frames rendered'));
        }
      }, 120);
    });
  }, []);

  useEffect(() => {
    if (phase !== 'camera') return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;

    attachStreamToVideo(stream).catch(() => {});
  }, [phase, attachStreamToVideo]);

  const startCamera = useCallback(async () => {
    setError('');
    stopCamera();
    setPhase('camera');
    try {
      // Let the camera view render before opening getUserMedia (iOS Safari is picky here).
      await new Promise(resolve => requestAnimationFrame(resolve));

      const attempts = [
        { video: { facingMode: { exact: 'environment' } }, audio: false },
        { video: { facingMode: { ideal: 'environment' } }, audio: false },
        { video: { facingMode: 'user' }, audio: false },
        { video: true, audio: false },
      ];

      // On iOS, facingMode can sometimes produce a black stream.
      // Add concrete deviceId attempts as a final fallback.
      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
      const videoInputs = devices.filter(d => d.kind === 'videoinput');
      const backInputs = videoInputs.filter(d => /back|rear|environment/i.test(d.label || ''));
      const frontInputs = videoInputs.filter(d => /front|user|facetime/i.test(d.label || ''));
      const otherInputs = videoInputs.filter(
        d => !backInputs.some(x => x.deviceId === d.deviceId) && !frontInputs.some(x => x.deviceId === d.deviceId)
      );
      [...backInputs, ...frontInputs, ...otherInputs].forEach(d => {
        attempts.push({ video: { deviceId: { exact: d.deviceId } }, audio: false });
      });

      let stream = null;
      for (const constraints of attempts) {
        let candidate = null;
        try {
          candidate = await navigator.mediaDevices.getUserMedia(constraints);
          await attachStreamToVideo(candidate);
          stream = candidate;
          break;
        } catch {
          if (videoRef.current) videoRef.current.srcObject = null;
          candidate?.getTracks().forEach(t => t.stop());
          stream = null;
          // Try next constraint profile.
        }
      }

      if (!stream) throw new Error('Unable to open camera stream');
      streamRef.current = stream;
    } catch {
      stopCamera();
      setPhase('select');
      setError('Camera opened but no video frames were received. Try reloading this page or use Safari tab mode instead of an in-app browser.');
    }
  }, [attachStreamToVideo, stopCamera]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const size = Math.min(vw, vh);
    const sx = (vw - size) / 2;
    const sy = (vh - size) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1080;
    canvas.getContext('2d').drawImage(video, sx, sy, size, size, 0, 0, 1080, 1080);
    canvas.toBlob(blob => {
      stopCamera();
      setCapturedBlob(blob);
      setCapturedObjectUrl(URL.createObjectURL(blob));
      setPhase('preview');
    }, 'image/jpeg', 0.92);
  }, [stopCamera]);

  const retake = useCallback(() => {
    if (capturedObjectUrl) URL.revokeObjectURL(capturedObjectUrl);
    setCapturedBlob(null);
    setCapturedObjectUrl(null);
    startCamera();
  }, [capturedObjectUrl, startCamera]);

  const upload = useCallback(async () => {
    if (!capturedBlob || !selectedTeam || !eventId) return;
    setUploading(true);
    setError('');
    try {
      const path = `memoryBooks/${eventId}/teams/${selectedTeam.id}.jpg`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, capturedBlob, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(sRef);

      const metaRef = doc(firestore, 'eventMemoryMeta', eventId);
      const metaSnap = await getDoc(metaRef);
      const entry = { teamId: selectedTeam.id, url };
      if (metaSnap.exists()) {
        const existing = (metaSnap.data().teamPhotos || []).filter(p => p.teamId !== selectedTeam.id);
        await updateDoc(metaRef, { teamPhotos: [...existing, entry] });
      } else {
        await setDoc(metaRef, { teamPhotos: [entry] });
      }

      await refreshMemoryTeamPhotos();

      URL.revokeObjectURL(capturedObjectUrl);
      setLastUploadedName(selectedTeam.teamName || `Team ${selectedTeam.teamNumber}`);
      setCapturedBlob(null);
      setCapturedObjectUrl(null);
      setSelectedTeamId('');
      setPhase('done');
    } catch (err) {
      console.error(err);
      setError('Upload failed — please try again.');
    } finally {
      setUploading(false);
    }
  }, [capturedBlob, capturedObjectUrl, selectedTeam, eventId, refreshMemoryTeamPhotos]);

  // ── Event picker ──────────────────────────────────────────────────────────
  if (!eventId) {
    return (
      <div className="pc-event-picker">
        <h2 className="pc-event-picker-title">Photo Capture — Select Event</h2>
        {events.length === 0 && <p className="pc-hint">Loading events…</p>}
        <div className="pc-event-list">
          {events.map(ev => (
            <button
              key={ev.id}
              className="pc-event-btn"
              onClick={() => setSearchParams({ eventId: ev.id })}
            >
              <span className="pc-event-title">{ev.title || ev.id}</span>
              {ev.startDate && <span className="pc-event-date">{ev.startDate}</span>}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Done flash ────────────────────────────────────────────────────────────
  if (phase === 'done') {
    const orphanEntries = memoryTeamPhotos.filter(
      (p) => !teams.some((t) => t.id === p.teamId)
    );
    return (
      <div className="pc-done">
        <div className="pc-done-check">✓</div>
        <div className="pc-done-label">Saved — {lastUploadedName}</div>
        <p className="pc-done-summary">{photoCountSummary}</p>
        <div className="pc-done-scroll" role="region" aria-label="Teams with a saved photo">
          <ul className="pc-done-team-list">
            {teamsWithPhotoSorted.map((t) => (
              <li key={t.id} className="pc-done-team-li">
                <span className="pc-done-team-num">#{t.teamNumber}</span>
                <span className="pc-done-team-name">{t.teamName || `Team ${t.teamNumber}`}</span>
              </li>
            ))}
            {orphanEntries.map((p) => (
              <li key={p.teamId} className="pc-done-team-li pc-done-team-li--orphan">
                <span className="pc-done-team-name">Team id {p.teamId.slice(0, 8)}…</span>
              </li>
            ))}
          </ul>
          {teamsWithPhotoSorted.length === 0 && orphanEntries.length === 0 && (
            <p className="pc-hint pc-done-empty">No team list loaded yet.</p>
          )}
        </div>
        <div className="pc-done-actions">
          <button type="button" className="pc-done-continue" onClick={() => setPhase('select')}>
            Continue
          </button>
          {eventId && (
            <Link
              className="pc-done-memory-link"
              to={`/admin/event-memory-book?eventId=${encodeURIComponent(eventId)}`}
            >
              Open Memory Book
            </Link>
          )}
        </div>
      </div>
    );
  }

  // ── Team selector ─────────────────────────────────────────────────────────
  if (phase === 'select') {
    return (
      <div className="pc-select">
        <div className="pc-select-header">
          <h2 className="pc-select-title">Photo Capture</h2>
          <div className="pc-select-header-actions">
            {eventId && (
              <Link
                className="pc-memory-link"
                to={`/admin/event-memory-book?eventId=${encodeURIComponent(eventId)}`}
              >
                Memory Book
              </Link>
            )}
            <button className="pc-change-event" onClick={() => setSearchParams({})}>
              Change event
            </button>
          </div>
        </div>
        {error && <div className="pc-error">{error}</div>}
        <p className="pc-hint">Pick a team, then open the camera.</p>
        {teams.length > 0 && (
          <p className="pc-photo-status" aria-live="polite">
            {photoCountSummary}
          </p>
        )}
        <div className="pc-team-list">
          {teams.map(t => (
            <button
              key={t.id}
              className={`pc-team-btn${selectedTeamId === t.id ? ' pc-team-btn--active' : ''}${teamIdsWithPhoto.has(t.id) ? ' pc-team-btn--has-photo' : ''}`}
              onClick={() => setSelectedTeamId(t.id)}
            >
              <span className="pc-team-num">#{t.teamNumber}</span>
              <span className="pc-team-name">{t.teamName || `Team ${t.teamNumber}`}</span>
              <span
                className={`pc-team-photo-tag${teamIdsWithPhoto.has(t.id) ? '' : ' pc-team-photo-tag--empty'}`}
                aria-label={teamIdsWithPhoto.has(t.id) ? 'Photo saved' : undefined}
              >
                {teamIdsWithPhoto.has(t.id) ? '✓ Photo' : '\u00a0'}
              </span>
              <span
                className={`pc-team-div${t.division ? '' : ' pc-team-div--empty-cell'}`}
              >
                {t.division || '\u00a0'}
              </span>
            </button>
          ))}
        </div>
        {selectedTeam && (
          <div className="pc-start-bar">
            <button className="pc-start-btn" onClick={startCamera}>
              Open Camera — #{selectedTeam.teamNumber} {selectedTeam.teamName || ''}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Camera viewfinder ─────────────────────────────────────────────────────
  if (phase === 'camera') {
    const teamLabel = selectedTeam
      ? `#${selectedTeam.teamNumber} — ${selectedTeam.teamName || `Team ${selectedTeam.teamNumber}`}`
      : '';
    return (
      <div className="pc-camera-root">
        <video ref={videoRef} className="pc-video" autoPlay playsInline muted />

        {/* Dimming + crop guides overlay */}
        <div className="pc-overlay" aria-hidden>
          <div className="pc-dim pc-dim--top" />
          <div className="pc-dim pc-dim--bottom" />
          <div className="pc-crop">
            {/* Corner bracket marks */}
            <div className="pc-corner pc-corner--tl" />
            <div className="pc-corner pc-corner--tr" />
            <div className="pc-corner pc-corner--bl" />
            <div className="pc-corner pc-corner--br" />
            {/*
              Poster element ghost guides — positions mirror TeamSharePoster.css
              as percentages of the 1080×1080 poster square:
                Score badge:  right 4.3%, top 3.9%, width 23.5%
                Team name:    left 4.3%, bottom 4.3%, width 64.3%
                QR + brand:   right 4.3%, bottom 4.3%, width 19%
            */}
            <div className="pc-guide pc-guide--score" />
            <div className="pc-guide pc-guide--team" />
            <div className="pc-guide pc-guide--qr" />
          </div>
        </div>

        {/* Top HUD — team identity */}
        <div className="pc-hud-top">
          <span className="pc-hud-team-label">{teamLabel}</span>
        </div>

        {/* Bottom HUD — shutter + cancel */}
        <div className="pc-hud-bottom">
          <button
            className="pc-shutter"
            onClick={capture}
            aria-label="Take photo"
          />
          <button
            className="pc-cancel"
            onClick={() => { stopCamera(); setPhase('select'); }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Preview ───────────────────────────────────────────────────────────────
  if (phase === 'preview') {
    return (
      <div className="pc-preview-root">
        {capturedObjectUrl && (
          <img className="pc-preview-img" src={capturedObjectUrl} alt="Preview of captured frame" />
        )}
        {error && <div className="pc-error pc-error--overlay">{error}</div>}
        <div className="pc-preview-actions">
          <button
            className="pc-preview-btn pc-preview-btn--retake"
            onClick={retake}
            disabled={uploading}
          >
            Retake
          </button>
          <button
            className="pc-preview-btn pc-preview-btn--save"
            onClick={upload}
            disabled={uploading}
          >
            {uploading ? 'Saving…' : 'Save Photo'}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
